import React, { useCallback, useEffect, useState } from 'react';
import { App, Button, Drawer, Form, Input, Select, Space, Table, Popconfirm, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import useComponentAttributeDefsApi from 'pages/liftlogic/v2/Components/useComponentAttributeDefsApi';
import useComponentTypesApi from 'pages/liftlogic/v2/Components/useComponentTypesApi';

const DATA_TYPES = [
  { value: 'text', label: 'Texto' },
  { value: 'number', label: 'Número' },
  { value: 'date', label: 'Fecha' },
  { value: 'select', label: 'Lista' },
  { value: 'bool', label: 'Sí/No' },
  { value: 'image', label: 'Imagen' },
];

// Gestiona el catálogo de atributos GLOBAL (liftlogic super-tenant). No hay override por-tenant (YAGNI — decisión 2026-06-19).
function ComponentAttributeDefsContent() {
  const api = useComponentAttributeDefsApi();
  const typesApi = useComponentTypesApi();
  const { message } = App.useApp();
  const [defs, setDefs] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const dataType = Form.useWatch('data_type', form);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.list();
      setDefs(data.defs || []);
    } catch (e) {
      message.error('No se pudo cargar el catálogo');
    } finally {
      setLoading(false);
    }
  }, [api, message]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    typesApi
      .list()
      .then((data) => setTypes(data.types || []))
      .catch(() => setTypes([]));
  }, [typesApi]);

  const onAdd = () => {
    setEditing(null);
    form.resetFields();
    setOpen(true);
  };
  const onEdit = (row) => {
    setEditing(row);
    form.setFieldsValue({
      ...row,
      options: (row.options || []).join(', '),
      applies_to_types: row.applies_to_types || [],
    });
    setOpen(true);
  };
  const onSubmit = async () => {
    const values = await form.validateFields();
    const payload = {
      key: values.key,
      label: values.label,
      data_type: values.data_type,
      options:
        values.data_type === 'select' && values.options
          ? values.options
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          : null,
      applies_to_types: values.applies_to_types?.length ? values.applies_to_types : null,
    };
    try {
      if (editing) await api.update(editing.id, payload);
      else await api.create(payload);
      message.success('Guardado');
      setOpen(false);
      refresh();
    } catch (e) {
      message.error(e?.response?.data?.message || 'Error al guardar');
    }
  };
  const onDelete = async (row) => {
    try {
      await api.remove(row.id);
      message.success('Eliminado');
      refresh();
    } catch (e) {
      message.error('No se pudo eliminar');
    }
  };

  const columns = [
    { title: 'Key', dataIndex: 'key' },
    { title: 'Etiqueta', dataIndex: 'label' },
    {
      title: 'Tipo',
      dataIndex: 'data_type',
      render: (t) => <Tag>{DATA_TYPES.find((d) => d.value === t)?.label || t}</Tag>,
    },
    { title: 'Opciones', dataIndex: 'options', render: (o) => (o || []).join(', ') },
    {
      title: 'Aplica a',
      dataIndex: 'applies_to_types',
      render: (a) =>
        a && a.length ? (
          a.map((k) => <Tag key={k}>{types.find((t) => t.key === k)?.label || k}</Tag>)
        ) : (
          <Tag>Global</Tag>
        ),
    },
    {
      title: 'Acciones',
      key: 'acc',
      render: (_, row) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => onEdit(row)} />
          <Popconfirm title="¿Eliminar definición?" onConfirm={() => onDelete(row)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Space style={{ marginBottom: 12 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={onAdd}>
          Nueva definición
        </Button>
      </Space>
      <Table rowKey="id" loading={loading} columns={columns} dataSource={defs} pagination={false} />
      <Drawer
        title={editing ? 'Editar atributo' : 'Nuevo atributo'}
        open={open}
        onClose={() => setOpen(false)}
        footer={
          <Space style={{ float: 'right' }}>
            <Button onClick={() => setOpen(false)}>Cancelar</Button>
            <Button type="primary" onClick={onSubmit}>
              Guardar
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="key"
            label="Key (snake_case)"
            rules={[
              { required: true },
              { pattern: /^[a-z0-9_]+$/, message: 'Solo minúsculas, números y _' },
            ]}
          >
            <Input disabled={!!editing} placeholder="vida_util_anios" />
          </Form.Item>
          <Form.Item name="label" label="Etiqueta" rules={[{ required: true }]}>
            <Input placeholder="Vida útil (años)" />
          </Form.Item>
          <Form.Item name="data_type" label="Tipo" rules={[{ required: true }]}>
            <Select options={DATA_TYPES} />
          </Form.Item>
          {dataType === 'select' && (
            <Form.Item
              name="options"
              label="Opciones (separadas por coma)"
              rules={[{ required: true }]}
            >
              <Input placeholder="operativo, degradado, a_reemplazar" />
            </Form.Item>
          )}
          <Form.Item name="applies_to_types" label="Aplica a tipos (vacío = global)">
            <Select
              mode="multiple"
              allowClear
              options={types.map((t) => ({ value: t.key, label: t.label }))}
              placeholder="Todos los tipos"
            />
          </Form.Item>
        </Form>
      </Drawer>
    </>
  );
}

export default ComponentAttributeDefsContent;
